import {
  mapLeadDetail,
  mapRawnoteEdited,
  type LeadDetail,
  type PersonEditInput,
  type RawnoteEditFields,
  type RawnoteEdited,
} from '@viq-admin/screens';
import { adminFetch } from './dataProvider';

/**
 * GET /v1/admin/leads/:id — the canonical fresh-lead fetch. The one endpoint
 * literal for this route: `views/LeadDetail/leadDetailApi.ts`'s
 * `LeadDetailApi.fetchLeadDetail` re-exports this rather than re-declaring
 * the path, and `crmUpload.ts`'s `updateLeadFields` calls it directly to get
 * a fresh `serverVersion` before composing a PATCH.
 */
export async function fetchLeadDetail(personId: string): Promise<LeadDetail> {
  return mapLeadDetail(await adminFetch<unknown>(`/v1/admin/leads/${personId}`));
}

/**
 * PATCH /v1/admin/leads/:id — edit the ten tear-sheet fields on a lead.
 * Returns the updated person (camel-cased, incl. serverVersion) by reusing the
 * shared mapper on the { person } response.
 */
export async function editLead(
  personId: string,
  fields: PersonEditInput,
  expectedVersion: number,
): Promise<LeadDetail['person']> {
  const body = await adminFetch<{ person: unknown }>(`/v1/admin/leads/${personId}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...fields, expectedVersion }),
  });
  return mapLeadDetail({ person: body.person }).person;
}

/**
 * POST /v1/admin/leads/:leadId/notes — add a note to a lead, including one
 * with no notes yet (the PATCH below can only correct a note that exists).
 * Same response mapping as `editRawnote`: the backend returns the raw snake_case
 * RETURNING row, which goes through the shared mapper.
 */
export async function createRawnote(leadId: string, text: string): Promise<RawnoteEdited> {
  const body = await adminFetch<{ note: unknown }>(`/v1/admin/leads/${leadId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  return mapRawnoteEdited(body.note);
}

/**
 * PATCH /v1/admin/leads/:leadId/notes/:noteId — correct one note's text and/or
 * its AI summary. Send ONLY the field being edited: a PATCH carrying both would
 * blank whichever one the operator did not touch.
 *
 * The response's `note` is the RETURNING row off the backend's UPDATE,
 * UNALIASED — i.e. snake_case, not the camelCase `RawnoteEdited` this
 * function returns. Typed `unknown` and run through the shared mapper, same
 * as `editLead` above does via `mapLeadDetail` on its `{ person }` response.
 * Do not "simplify" this back to `body.note` — that would silently produce a
 * `RawnoteEdited` with `serverVersion`/`originalText`/`updatedAt` undefined.
 */
export async function editRawnote(
  leadId: string,
  noteId: string,
  fields: RawnoteEditFields,
  expectedVersion: number,
): Promise<RawnoteEdited> {
  const body = await adminFetch<{ note: unknown }>(
    `/v1/admin/leads/${leadId}/notes/${noteId}`,
    { method: 'PATCH', body: JSON.stringify({ ...fields, expectedVersion }) },
  );
  return mapRawnoteEdited(body.note);
}
